import { Children, forwardRef, HTMLAttributes, ReactElement, Ref, useRef, useState } from "react";
import { Virtuoso } from "react-virtuoso";
import Close from "@mui/icons-material/Close";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import {
  Alert,
  autocompleteClasses,
  Box,
  Checkbox,
  chipClasses,
  createFilterOptions,
  inputBaseClasses,
  Autocomplete as MuiAutocomplete,
  outlinedInputClasses,
  Paper,
  selectClasses,
  Snackbar,
  styled,
  TextField,
} from "@mui/material";
import { AutocompleteProps as MuiAutocompleteProps } from "@mui/material/Autocomplete";
import pluralize from "pluralize";
import { omit } from "ramda";
import TooltipChip from "~/components/TooltipChip";

const StyledPaper = styled(Paper)({
  [`& .${autocompleteClasses.option}, .${autocompleteClasses.noOptions}`]: { fontSize: 14 },
});

const ListboxComponent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLElement>>((props, ref) => {
  const [height, setHeight] = useState(0);
  const virtuosoRef = useRef(null);
  const { children, ...other } = props;
  const items = Children.toArray(children);

  return (
    <Box
      ref={ref}
      {...other}
      sx={{
        [`&.${autocompleteClasses.listbox}`]: {
          p: 0,
        },
      }}
    >
      <Virtuoso
        ref={virtuosoRef}
        totalListHeightChanged={setHeight}
        style={{
          height: height ? `min(40vh, ${height + 16}px)` : 1,
        }}
        data={items}
        itemContent={(_, item) => item}
        components={{
          // Add standard menu list top/bottom padding to scrollable area
          /* eslint-disable react/display-name */
          List: forwardRef((listProps, listRef) => (
            <Box py={1}>
              <Box ref={listRef} {...listProps} />
            </Box>
          )),
        }}
      />
    </Box>
  );
});

ListboxComponent.displayName = "ListboxComponent";

interface AutocompleteInputProps<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
> extends Omit<MuiAutocompleteProps<T, Multiple, DisableClearable, false>, "renderInput"> {
  onChange: (...event: any[]) => void;
  name: string;
  placeholder?: string;
  autoFocus?: boolean;
  showCheckmarks?: boolean;
}

const Autocomplete = <
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
>(
  props: AutocompleteInputProps<T, Multiple, DisableClearable>,
  ref: Ref<HTMLDivElement>
) => {
  const [pasteAlertMessage, setPasteAlertMessage] = useState<string | null>(null);

  const handlePaste = (event: React.ClipboardEvent) => {
    if (!props.multiple) {
      // only handle paste for multi-select
      return;
    }
    const pastedText = event.clipboardData.getData("text");
    if (!pastedText) {
      return;
    }
    const pastedValues = pastedText
      .split(/[,\n]/)
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
    if (pastedValues.length <= 1) {
      // if only one value is pasted, use normal select option search
      return;
    }

    event.preventDefault();

    const currentValue = (props.value as T[]) || [];
    const matchingOptions: T[] = [];
    const unmatchedValues: string[] = [];

    pastedValues.forEach((pastedValue) => {
      const matchingOption = props.options?.find((option) => {
        const optionLabel = props.getOptionLabel ? props.getOptionLabel(option) : String(option);
        return optionLabel.toLowerCase() === pastedValue.toLowerCase();
      });
      if (matchingOption && !currentValue.includes(matchingOption)) {
        matchingOptions.push(matchingOption);
      } else if (!matchingOption) {
        console.warn(`No matching options found for: ${pastedValue}`);
        unmatchedValues.push(pastedValue);
      }
    });

    let alertMessage =
      matchingOptions.length > 0
        ? `${matchingOptions.length} ${pluralize(
            "value",
            matchingOptions.length
          )} added successfully. `
        : "";

    if (unmatchedValues.length > 0) {
      alertMessage += `${unmatchedValues.length} ${pluralize(
        "value",
        unmatchedValues.length
      )} did not match.`;
    }

    if (matchingOptions.length > 0) {
      const newValue = [...currentValue, ...matchingOptions];
      props.onChange(null, newValue);
      setPasteAlertMessage(alertMessage);
    }
  };

  return (
    <>
      <MuiAutocomplete<T, Multiple, DisableClearable>
        ref={ref}
        size="small"
        limitTags={1}
        disableCloseOnSelect
        popupIcon={<KeyboardArrowDown sx={{ fontSize: 18 }} />}
        clearIcon={<Close sx={{ fontSize: 14 }} />}
        filterOptions={createFilterOptions({
          limit: 500,
        })}
        renderOption={
          props.multiple && props.showCheckmarks
            ? (optionProps, option, { selected }) => {
                const { key, ...rest } = optionProps;
                const label = props.getOptionLabel ? props.getOptionLabel(option) : String(option);
                return (
                  <Box
                    component="li"
                    key={key}
                    {...rest}
                    onMouseDown={(e) => e.preventDefault()}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Checkbox checked={selected} size="small" sx={{ p: 0 }} />
                    {label}
                  </Box>
                );
              }
            : undefined
        }
        ListboxComponent={ListboxComponent}
        PaperComponent={StyledPaper}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            const label = props.getOptionLabel ? props.getOptionLabel(option) : String(option);
            return <TooltipChip size="small" key={key} {...tagProps} label={label} />;
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            name={props.name}
            placeholder={props.placeholder}
            autoFocus={props.autoFocus}
            onPaste={handlePaste}
            sx={{
              [`& .${outlinedInputClasses.root}.${autocompleteClasses.inputRoot} .${autocompleteClasses.input}`]:
                {
                  paddingLeft: "2px",
                },
            }}
            slotProps={{
              input: {
                ...params.InputProps,
                sx: {
                  minHeight: 33,
                  fontSize: 14,
                  minWidth: "100%",
                  maxWidth: "min-content",
                  [`&.${outlinedInputClasses.root}.${autocompleteClasses.inputRoot}.${inputBaseClasses.sizeSmall}`]:
                    {
                      paddingTop: "4.5px",
                      paddingBottom: "4.5px",
                    },
                  // Handle keeping height aligned other filter inputs when unfocused
                  [`&:not(.${inputBaseClasses.focused})`]: props.multiple
                    ? {
                        maxHeight: 33,
                        // Make room for limitTags text
                        [`& .${chipClasses.root}`]: {
                          maxWidth: "calc(100% - 50px)",
                          height: 20,
                        },
                        // Minimize input padding when chips are present, but keep min-height for placeholder visibility
                        [`& .${inputBaseClasses.inputSizeSmall}.${inputBaseClasses.input}`]: {
                          minHeight: "1.4375em", // Preserve line-height for placeholder visibility
                          p: 0,
                        },
                      }
                    : {
                        maxHeight: 33,
                      },
                  // Allow vertical expansion when focused in multiple mode to show all tags
                  [`&.${inputBaseClasses.focused}`]: props.multiple
                    ? {
                        [`& .${chipClasses.root}`]: {
                          height: 20,
                        },
                        [`& .${inputBaseClasses.inputSizeSmall}.${inputBaseClasses.input}`]: {
                          p: 0,
                          minHeight: 0,
                        },
                      }
                    : {
                        maxHeight: 33,
                      },
                  [`&.${selectClasses.disabled}`]: {
                    display: "none",
                  },
                  [`& .${chipClasses.label}`]: { maxWidth: 500 },
                },
              },
            }}
            variant="outlined"
          />
        )}
        // Avoid using name prop on invalid div element,
        // instead it's set explicitly on the TextField (input)
        {...omit(["name", "showCheckmarks"], props)}
      />
      <Snackbar
        open={!!pasteAlertMessage}
        autoHideDuration={5000}
        onClose={() => setPasteAlertMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="info">{pasteAlertMessage}</Alert>
      </Snackbar>
    </>
  );
};

// Use forwardRef but cast the returned component to a generic type that matches the props signature.
// This allows parent components to specify the generics without type errors.
const AutocompleteInput = forwardRef(Autocomplete) as <
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
>(
  props: AutocompleteInputProps<T, Multiple, DisableClearable> & { ref?: Ref<HTMLDivElement> }
) => ReactElement | null;

export default AutocompleteInput;
